import { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { CATEGORIAS_COMPRA, createPurchase, getPurchase, updatePurchase } from '../services/purchasesService';
import { friendlyError } from '../lib/errors';
import LoadingState from '../components/ui/LoadingState';

const EMPTY = {
  tipoDocumento: '01',
  serie: '',
  correlativo: '',
  fechaEmision: '',
  proveedorRuc: '',
  proveedorRazonSocial: '',
  subtotal: '',
  igv: '',
  total: '',
  moneda: 'PEN',
  categoria: 'inventario',
  descripcion: '',
};

export default function PurchaseFormPage() {
  const { id } = useParams();
  const isEdit = Boolean(id);
  const { ruc } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState(EMPTY);
  const [loading, setLoading] = useState(isEdit);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    if (!id) return;
    setLoading(true);
    setError(null);
    try {
      const c = await getPurchase(id);
      setForm({
        tipoDocumento: c.tipoDocumento || '01',
        serie: c.serie || '',
        correlativo: c.correlativo || '',
        fechaEmision: c.fechaEmision || '',
        proveedorRuc: c.proveedorRuc || '',
        proveedorRazonSocial: c.proveedorRazonSocial || '',
        subtotal: c.subtotal ?? '',
        igv: c.igv ?? '',
        total: c.total ?? '',
        moneda: c.moneda || 'PEN',
        categoria: c.categoria || 'inventario',
        descripcion: c.descripcion || '',
      });
    } catch (e) {
      setError(friendlyError(e));
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    load();
  }, [load]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const onSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError(null);
    try {
      if (isEdit) {
        await updatePurchase(id, form);
        navigate(`/admin/compras/${id}`);
      } else {
        const ref = await createPurchase({ ruc, data: form });
        navigate(`/admin/compras/${ref.id}`);
      }
    } catch (err) {
      setError(friendlyError(err));
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <LoadingState label="Cargando compra…" />;

  return (
    <div className="page narrow">
      <header className="page-header">
        <div>
          <h1>{isEdit ? 'Editar compra' : 'Nueva compra'}</h1>
          <p className="muted">
            Modelo real de <code>compras</code>. El stock se actualiza aparte con Inventario / aumentarStock.
          </p>
        </div>
      </header>

      <form className="form-card" onSubmit={onSubmit}>
        <div className="form-grid">
          <label>
            Tipo documento *
            <select value={form.tipoDocumento} onChange={(e) => set('tipoDocumento', e.target.value)}>
              <option value="01">Factura</option>
              <option value="03">Boleta</option>
              <option value="07">Nota de crédito</option>
              <option value="91">Ticket</option>
              <option value="99">Otros</option>
            </select>
          </label>
          <label>
            Serie *
            <input value={form.serie} onChange={(e) => set('serie', e.target.value)} required />
          </label>
          <label>
            Correlativo *
            <input value={form.correlativo} onChange={(e) => set('correlativo', e.target.value)} required />
          </label>
          <label>
            Fecha emisión
            <input type="date" value={form.fechaEmision} onChange={(e) => set('fechaEmision', e.target.value)} />
          </label>
          <label>
            RUC proveedor
            <input value={form.proveedorRuc} onChange={(e) => set('proveedorRuc', e.target.value)} />
          </label>
          <label>
            Razón social proveedor
            <input value={form.proveedorRazonSocial} onChange={(e) => set('proveedorRazonSocial', e.target.value)} />
          </label>
          <label>
            Subtotal
            <input type="number" step="0.01" value={form.subtotal} onChange={(e) => set('subtotal', e.target.value)} />
          </label>
          <label>
            IGV
            <input type="number" step="0.01" value={form.igv} onChange={(e) => set('igv', e.target.value)} />
          </label>
          <label>
            Total *
            <input type="number" step="0.01" min="0.01" value={form.total} onChange={(e) => set('total', e.target.value)} required />
          </label>
          <label>
            Moneda
            <select value={form.moneda} onChange={(e) => set('moneda', e.target.value)}>
              <option value="PEN">PEN</option>
              <option value="USD">USD</option>
            </select>
          </label>
          <label>
            Categoría
            <select value={form.categoria} onChange={(e) => set('categoria', e.target.value)}>
              {CATEGORIAS_COMPRA.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.label}
                </option>
              ))}
            </select>
          </label>
          <label className="span-2">
            Descripción
            <textarea rows={2} value={form.descripcion} onChange={(e) => set('descripcion', e.target.value)} />
          </label>
        </div>

        {error ? <div className="alert error">{error}</div> : null}

        <div className="form-actions">
          <button type="button" className="btn btn-ghost" onClick={() => navigate(-1)}>
            Cancelar
          </button>
          <button type="submit" className="btn btn-primary" disabled={saving}>
            {saving ? 'Guardando…' : 'Guardar compra'}
          </button>
        </div>
      </form>
    </div>
  );
}
